import { useCallback } from 'react';

import { useClearRefinements, useInstantSearch, useRefinementList } from 'react-instantsearch';

import { useTracking } from '@change-corgi/core-react/tracking';

import {
	INSTANT_SEARCH_LOCATION_CITY_FILTER,
	INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
} from 'src/app/pages/search/shared/consts';
import type { LocationItemTypeEnum } from 'src/app/pages/search/shared/types';

type Result = ModelHookResult<
	undefined,
	{
		selectLocation: (value: string, type: LocationItemTypeEnum) => void;
	}
>;

export function useSelectLocation(): Result {
	const track = useTracking();

	const { indexUiState } = useInstantSearch();

	const { refine: clearRefine } = useClearRefinements({
		includedAttributes: [INSTANT_SEARCH_LOCATION_CITY_FILTER, INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER],
	});

	const { refine: filterByCountry } = useRefinementList({
		attribute: INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
	});

	const { refine: filterByCity } = useRefinementList({
		attribute: INSTANT_SEARCH_LOCATION_CITY_FILTER,
	});

	return {
		actions: {
			selectLocation: useCallback(
				(value: string, type: LocationItemTypeEnum) => {
					void track('search', {
						context: 'location_filter',
						location_value: value,
						location_type: type,
						q: indexUiState.query || '',
					});
					clearRefine();
					if (type === 'country') {
						filterByCountry(value);
					}
					if (type === 'city') {
						filterByCity(value);
					}
				},
				[track, indexUiState.query, clearRefine, filterByCountry, filterByCity],
			),
		},
	};
}
